import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import { Badge } from '../../components/common/Badge';
import { StatCard } from '../../components/cards/StatCard';
import { PropertyForm } from '../../components/forms/PropertyForm';
import { PropertyEmailComposer } from '../../components/forms/PropertyEmailComposer';
import {
   LayoutDashboard, MessageSquare, Plus, BarChart3,
   Building, Settings, CheckCircle, Clock, Search, MoreVertical,
   User, ChevronRight, X, ChevronLeft, UserCheck, AlertCircle, XCircle, Send,
   FileText, Mail, GraduationCap, Eye
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { InternalNotes } from '../../components/common/InternalNotes';
import { UserRole } from '../../types';

// Simple Toast Component
const Toast: React.FC<{ message: string; type: 'success' | 'error'; onClose: () => void }> = ({ message, type, onClose }) => (
   <div className={`fixed top-24 right-4 z-[100] bg-white border shadow-xl rounded-lg p-4 flex items-start gap-3 max-w-sm animate-fadeIn ${type === 'success' ? 'border-green-100' : 'border-red-100'}`}>
      <div className={`mt-0.5 ${type === 'success' ? 'text-green-500' : 'text-red-500'}`}>
         {type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
      </div>
      <div className="flex-1">
         <h4 className={`font-bold text-sm ${type === 'success' ? 'text-green-900' : 'text-red-900'}`}>
            {type === 'success' ? 'Success' : 'Notice'}
         </h4>
         <p className="text-sm text-gray-600">{message}</p>
      </div>
      <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
         <X className="w-4 h-4" />
      </button>
   </div>
);

export const AgentDashboard: React.FC = () => {
   const [activeTab, setActiveTab] = useState('overview');
   const { properties, deleteProperty, messages, sendMessage, users, bookings, confirmBooking, rejectBooking } = useData();
   const { user } = useAuth();

   // Toast State
   const [toast, setToast] = useState<{ show: boolean, message: string, type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });

   // Filter properties for this agent (Primary or Secondary)
   const myProperties = properties.filter(p => p.agentId === user?.id || p.coAgentId === user?.id);
   const myBookings = bookings.filter(b => myProperties.some(p => p.id === b.propertyId));

   // Filter Bookings by Status
   const pendingRequests = myBookings.filter(b => b.status === 'PENDING');
   const activeTenants = myBookings.filter(b => b.status === 'CONFIRMED' || b.status === 'COMPLETED');
   const declinedRequests = myBookings.filter(b => b.status === 'CANCELLED'); // New Filter

   // States
   const [selectedChatUser, setSelectedChatUser] = useState<string | null>(null);
   const [chatInput, setChatInput] = useState('');
   const [viewingProfile, setViewingProfile] = useState<string | null>(null);
   const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
   const [isEmailComposerOpen, setIsEmailComposerOpen] = useState(false);

   // Auto-hide toast
   useEffect(() => {
      if (toast.show) {
         const timer = setTimeout(() => setToast({ ...toast, show: false }), 3000);
         return () => clearTimeout(timer);
      }
   }, [toast.show]);

   // Handlers
   const handleApprove = async (bookingId: string) => {
      console.log("Attempting to approve booking:", bookingId);
      if (window.confirm("Confirm payment received? This will mark the room/property as rented.")) {
         console.log("User confirmed approval dialog.");
         const success = await confirmBooking(bookingId);
         console.log("Approve result received:", success);
         if (success) {
            setToast({ show: true, message: "Booking approved! Moved to Active Tenants.", type: 'success' });
         } else {
            setToast({ show: true, message: "Failed to approve booking. Check console for details.", type: 'error' });
         }
      } else {
         console.log("User cancelled approval dialog.");
      }
   };

   const handleDecline = async (bookingId: string) => {
      console.log("Attempting to decline booking:", bookingId);
      if (window.confirm("Are you sure you want to decline this request?")) {
         console.log("User confirmed decline dialog.");
         const success = await rejectBooking(bookingId);
         console.log("Decline result received:", success);
         if (success) {
            setToast({ show: true, message: "Request declined.", type: 'error' });
         } else {
            setToast({ show: true, message: "Failed to decline booking. Check console for details.", type: 'error' });
         }
      } else {
         console.log("User cancelled decline dialog.");
      }
   };

   // Derive Conversations
   const conversations = Array.from(new Set([...messages.map(m => m.senderId), ...messages.map(m => m.receiverId)]))
      .filter(id => id !== user?.id)
      .filter(id => {
         if (!user) return false;
         // If admin is viewing, they should see all conversations for their assigned properties
         // OR any conversation where they are the sender/receiver
         if (user.role === UserRole.ADMIN) {
            const relatedToMyProps = messages.some(m =>
               (m.senderId === id || m.receiverId === id) &&
               m.propertyId && myProperties.some(p => p.id === m.propertyId)
            );
            const directlyInvolved = messages.some(m =>
               (m.senderId === id && m.receiverId === user.id) ||
               (m.senderId === user.id && m.receiverId === id)
            );
            return relatedToMyProps || directlyInvolved;
         }

         // For Agents: See their own chats OR chats related to properties they manage (Primary or Co-Agent)
         const relatedToMyProps = messages.some(m =>
            (m.senderId === id || m.receiverId === id) &&
            m.propertyId && myProperties.some(p => p.id === m.propertyId)
         );
         const directlyInvolved = messages.some(m =>
            (m.senderId === id && m.receiverId === user.id) ||
            (m.senderId === user.id && m.receiverId === id)
         );

         return relatedToMyProps || directlyInvolved;
      })
      .map(id => {
         const lastMsg = messages.filter(m => m.senderId === id || m.receiverId === id).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
         const contactUser = users.find(u => u.id === id);
         return {
            userId: id,
            name: contactUser?.name || lastMsg?.senderName || `Student ${id.substring(0, 4)}`,
            lastMessage: lastMsg?.content,
            timestamp: lastMsg?.createdAt,
            unread: !lastMsg?.isRead && lastMsg?.receiverId === user?.id
         };
      });

   const activeConversation = selectedChatUser ? messages.filter(m => (m.senderId === user?.id && m.receiverId === selectedChatUser) || (m.senderId === selectedChatUser && m.receiverId === user?.id)).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) : [];

   const handleSendMessage = async () => {
      if (!selectedChatUser || !chatInput.trim()) return;
      await sendMessage(selectedChatUser, chatInput);
      setChatInput('');
   };

   const sidebarItems = [
      { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'listings', label: 'My Listings', icon: Building },
      { id: 'leads', label: 'Leads & Tenants', icon: UserCheck },
      { id: 'registered-students', label: 'Registered Students', icon: GraduationCap },
      { id: 'add-listing', label: 'Add New', icon: Plus },
      { id: 'email-properties', label: 'Email Properties', icon: Mail },
      { id: 'forms', label: 'Forms', icon: FileText, onClick: () => window.location.hash = '#/agent/forms' },
      { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      { id: 'messages', label: 'Messages', icon: MessageSquare },
      { id: 'settings', label: 'Settings', icon: Settings },
   ];

   // --- PENDING STATE VIEW ---
   if (user?.status === 'PENDING') {
      return (
         <DashboardLayout title="Agent Dashboard" sidebarItems={sidebarItems.slice(0, 1)} activeTab="overview" onTabChange={() => { }}>
            <div className="max-w-2xl mx-auto py-12">
               <Card className="p-8 text-center border-t-4 border-t-yellow-400 shadow-lg">
                  <div className="w-20 h-20 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-6">
                     <Clock className="w-10 h-10 text-yellow-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Under Review</h2>
                  <p className="text-gray-600 mb-8 leading-relaxed max-w-lg mx-auto">
                     Thanks for registering as an agent! Our admin team is currently verifying your agency credentials.
                     This process typically takes 24-48 hours. You will receive an email once approved.
                  </p>
                  <div className="mt-8">
                     <Button variant="outline" onClick={() => window.location.href = '#/contact'}>Contact Support</Button>
                  </div>
               </Card>
            </div>
         </DashboardLayout>
      );
   }

   // --- APPROVED DASHBOARD ---
   return (
      <DashboardLayout title="Agent Dashboard" sidebarItems={sidebarItems} activeTab={activeTab} onTabChange={(tab) => {
         if (tab === 'forms') {
            window.location.hash = '#/agent/forms';
         } else {
            setActiveTab(tab);
         }
      }}>

         {toast.show && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />}

         {/* OVERVIEW TAB */}
         {activeTab === 'overview' && (
            <div className="space-y-8 animate-fadeIn">
               {/* Header Status */}
               <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm gap-4">
                  <div>
                     <h2 className="text-2xl font-bold text-gray-900">Welcome, {user?.name}</h2>
                     <div className="flex gap-2 mt-2">
                        <p className="text-gray-500 flex items-center gap-1 text-sm">
                           <CheckCircle className="w-4 h-4 text-green-500" /> Verified Agent
                        </p>
                        {user?.isTrusted && (
                           <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full font-bold">Trusted Partner</span>
                        )}
                     </div>
                  </div>
                  <div className="flex gap-3 w-full md:w-auto">
                     <Button onClick={() => setActiveTab('add-listing')} className="w-full md:w-auto"><Plus className="w-4 h-4 mr-2" /> Add Listing</Button>
                     <Button variant="outline" onClick={() => setActiveTab('messages')} className="w-full md:w-auto">Messages</Button>
                  </div>
               </div>

               {/* Quick Stats */}
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard label="Active Listings" value={myProperties.filter(p => p.status === 'AVAILABLE').length.toString()} icon={Building} />
                  <StatCard label="Pending Requests" value={pendingRequests.length.toString()} icon={UserCheck} color={pendingRequests.length > 0 ? "red" : "blue"} />
                  <StatCard label="New Inquiries" value={conversations.length.toString()} icon={MessageSquare} trend="Real-time" />
                  <StatCard label="Total Tenants" value={activeTenants.length.toString()} icon={User} color="green" />
               </div>

               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Pending Requests Preview */}
                  <Card className="p-6">
                     <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-900">Latest Booking Requests</h3>
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab('leads')}>View All</Button>
                     </div>
                     <div className="space-y-4">
                        {pendingRequests.slice(0, 3).map(req => {
                           const student = users.find(u => u.id === req.studentId);
                           const prop = myProperties.find(p => p.id === req.propertyId);
                           return (
                              <div key={req.id} className="flex items-center justify-between p-3 bg-yellow-50/50 rounded-lg border border-yellow-100">
                                 <div>
                                    <p className="font-bold text-gray-900">{student?.name || 'Unknown Student'}</p>
                                    <p className="text-xs text-gray-600">{prop?.title}</p>
                                 </div>
                                 <Button size="sm" onClick={() => setActiveTab('leads')}>Review</Button>
                              </div>
                           )
                        })}
                        {pendingRequests.length === 0 && <p className="text-gray-500 text-sm py-4 text-center">No pending requests.</p>}
                     </div>
                  </Card>

                  {/* Recent Messages */}
                  <Card className="p-6">
                     <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-900">Recent Messages</h3>
                        <Button variant="ghost" size="sm" onClick={() => setActiveTab('messages')}>View All</Button>
                     </div>
                     <div className="space-y-4">
                        {conversations.slice(0, 3).map(conv => (
                           <div key={conv.userId} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0 hover:bg-gray-50 p-2 rounded transition-colors cursor-pointer" onClick={() => { setActiveTab('messages'); setSelectedChatUser(conv.userId); }}>
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs flex-shrink-0">
                                 {conv.name.charAt(0)}
                              </div>
                              <div className="flex-1 min-w-0">
                                 <div className="flex justify-between">
                                    <p className="text-sm font-bold text-gray-900">{conv.name}</p>
                                    <span className="text-xs text-gray-400">{new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                 </div>
                                 <p className="text-xs text-gray-500 mb-1 truncate">{conv.lastMessage}</p>
                              </div>
                           </div>
                        ))}
                        {conversations.length === 0 && <p className="text-gray-500 text-sm py-4 text-center">No messages yet.</p>}
                     </div>
                  </Card>
               </div>
            </div>
         )}

         {/* ADD LISTING TAB */}
         {activeTab === 'add-listing' && (
            <div className="max-w-3xl mx-auto animate-fadeIn">
               <div className="mb-6 flex items-center gap-2">
                  <Button variant="ghost" onClick={() => setActiveTab('listings')} className="p-2"><MoreVertical className="w-5 h-5 rotate-90" /></Button>
                  <h2 className="text-2xl font-bold text-gray-900">Create New Listing</h2>
               </div>
               <PropertyForm onSuccess={() => setActiveTab('listings')} />
            </div>
         )}

         {/* LISTINGS TAB */}
         {activeTab === 'listings' && !editingPropertyId && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div>
                     <h2 className="text-2xl font-bold text-gray-900">My Listings</h2>
                     <p className="text-gray-500">Manage your property portfolio.</p>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                     <div className="relative flex-1 sm:w-64">
                        <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                        <input type="text" placeholder="Search properties..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none" />
                     </div>
                     <Button onClick={() => setActiveTab('add-listing')}><Plus className="w-4 h-4 mr-2" /> Add</Button>
                  </div>
               </div>

               {myProperties.length > 0 ? (
                  <div className="grid gap-4">
                     {myProperties.map(p => (
                        <Card key={p.id} className="p-4 flex flex-col sm:flex-row gap-6 items-center hover:shadow-md transition-shadow group">
                           <div className="relative w-full sm:w-48 h-32 overflow-hidden rounded-lg">
                              <img src={p.image} className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500" />
                              <div className="absolute top-2 right-2">
                                 <Badge color={p.status === 'AVAILABLE' ? 'green' : (p.status === 'PENDING' ? 'yellow' : 'gray')}>
                                    {p.status}
                                 </Badge>
                              </div>
                           </div>
                           <div className="flex-1 w-full text-center sm:text-left">
                              <div className="flex flex-col sm:flex-row justify-between items-start">
                                 <h3 className="font-bold text-gray-900 text-lg mb-1">{p.title}</h3>
                                 <span className="font-bold text-[#2f5c96]">R{p.price}/mo</span>
                              </div>
                              <p className="text-sm text-gray-500 mb-2">{p.location}</p>
                              <div className="flex flex-wrap gap-2 justify-center sm:justify-start mb-2">
                                 <Badge color="blue">{p.type}</Badge>
                                 <span className="text-xs text-gray-400 flex items-center">
                                    {p.bedrooms} Beds • {p.bathrooms} Baths
                                 </span>
                              </div>
                           </div>
                           <div className="flex gap-2 w-full sm:w-auto border-t sm:border-t-0 pt-4 sm:pt-0 mt-2 sm:mt-0">
                              <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => setEditingPropertyId(p.id)}>Edit</Button>
                              <Button
                                 variant="ghost"
                                 size="sm"
                                 onClick={() => {
                                    if (window.confirm('Archive this property? It will be hidden from students.')) {
                                       deleteProperty(p.id);
                                       setToast({ show: true, message: "Property archived.", type: 'success' });
                                    }
                                 }}
                                 className="text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                              >
                                 Archive
                              </Button>
                           </div>
                        </Card>
                     ))}
                  </div>
               ) : (
                  <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                     <Building className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400" />
                     <h3 className="text-lg font-bold text-gray-900 mb-2">No listings yet</h3>
                     <p className="text-gray-500 mb-6">Start by adding your first property to the platform.</p>
                     <Button onClick={() => setActiveTab('add-listing')}>Create Listing</Button>
                  </div>
               )}
            </div>
         )}

         {/* EDIT LISTING SUB-VIEW */}
         {activeTab === 'listings' && editingPropertyId && (
            <div className="max-w-3xl mx-auto animate-fadeIn">
               <div className="mb-6 flex items-center gap-2">
                  <Button variant="ghost" onClick={() => setEditingPropertyId(null)} className="p-2 -ml-2"><ChevronLeft className="w-5 h-5" /> Back</Button>
                  <h2 className="text-2xl font-bold text-gray-900">Edit Listing</h2>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                     <PropertyForm
                        initialData={properties.find(p => p.id === editingPropertyId)}
                        onSuccess={() => setEditingPropertyId(null)}
                     />
                  </div>
                  <div className="md:col-span-1">
                     <InternalNotes targetId={editingPropertyId} targetType="property" />
                  </div>
               </div>
            </div>
         )}

         {/* LEADS & TENANTS TAB */}
         {activeTab === 'leads' && (
            <div className="space-y-8 animate-fadeIn">

               {/* Pending Section */}
               <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                     <Clock className="w-5 h-5 text-orange-500" /> Pending Requests
                  </h2>
                  {pendingRequests.length > 0 ? (
                     <div className="grid gap-4">
                        {pendingRequests.map(req => {
                           const student = users.find(u => u.id === req.studentId);
                           const prop = myProperties.find(p => p.id === req.propertyId);
                           const room = prop?.rooms?.find(r => r.id === req.roomId);

                           return (
                              <Card key={req.id} className="p-6 border-l-4 border-l-orange-400">
                                 <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                    <div>
                                       <div className="flex items-center gap-2">
                                          <h3 className="text-lg font-bold text-gray-900">{student?.name || 'Unknown Applicant'}</h3>
                                          <Badge color="yellow">Pending Payment</Badge>
                                       </div>
                                       <div className="text-sm text-gray-600 mt-2 space-y-1">
                                          <p><span className="font-semibold">Property:</span> {prop?.title}</p>
                                          {room && <p><span className="font-semibold">Room:</span> {room.name} (R{room.price})</p>}
                                          {!room && <p><span className="font-semibold">Unit:</span> Whole Unit (R{prop?.price})</p>}
                                          <p><span className="font-semibold">Contact:</span> {req.status === 'CONFIRMED' ? (student?.phoneNumber || 'N/A') : 'Masked (Confirm to view)'} • {req.status === 'CONFIRMED' ? student?.email : '••••@••••.com'}</p>
                                          <p><span className="font-semibold">Date Requested:</span> {new Date(req.date).toLocaleDateString()}</p>
                                       </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                                       <Button variant="outline" type="button" onClick={() => { setSelectedChatUser(req.studentId); setActiveTab('messages'); }}>Message</Button>
                                       <Button
                                          type="button"
                                          variant="ghost"
                                          className="text-red-600 hover:bg-red-50 border border-red-200"
                                          onClick={() => handleDecline(req.id)}
                                       >
                                          Decline
                                       </Button>
                                       <Button
                                          type="button"
                                          onClick={() => handleApprove(req.id)}
                                          className="bg-green-600 hover:bg-green-700 text-white"
                                       >
                                          Approve
                                       </Button>
                                    </div>
                                 </div>
                              </Card>
                           )
                        })}
                     </div>
                  ) : (
                     <Card className="p-8 text-center bg-gray-50 border-dashed">
                        <p className="text-gray-500">No pending booking requests.</p>
                     </Card>
                  )}
               </div>

               {/* Confirmed Tenants Section */}
               <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                     <UserCheck className="w-5 h-5 text-green-600" /> Active Tenants
                  </h2>
                  {activeTenants.length > 0 ? (
                     <div className="grid gap-4">
                        {activeTenants.map(req => {
                           const student = users.find(u => u.id === req.studentId);
                           const prop = myProperties.find(p => p.id === req.propertyId);
                           const room = prop?.rooms?.find(r => r.id === req.roomId);

                           return (
                              <Card key={req.id} className="p-6 border-l-4 border-l-green-500">
                                 <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                    <div className="flex items-center gap-4">
                                       <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">
                                          {student?.name.charAt(0)}
                                       </div>
                                       <div>
                                          <h3 className="font-bold text-gray-900">{student?.name}</h3>
                                          <p className="text-sm text-gray-600">
                                             {prop?.title} {room ? `• ${room.name}` : ''}
                                          </p>
                                       </div>
                                    </div>
                                    <div>
                                       <Badge color="green">Rented</Badge>
                                    </div>
                                 </div>
                              </Card>
                           )
                        })}
                     </div>
                  ) : (
                     <Card className="p-8 text-center bg-gray-50 border-dashed">
                        <p className="text-gray-500">No active tenants yet.</p>
                     </Card>
                  )}
               </div>

               {/* Declined Requests History */}
               {declinedRequests.length > 0 && (
                  <div className="mt-8 border-t border-gray-200 pt-8 opacity-75 hover:opacity-100 transition-opacity">
                     <h2 className="text-lg font-bold text-gray-500 mb-4 flex items-center gap-2">
                        <XCircle className="w-5 h-5" /> Declined History
                     </h2>
                     <div className="grid gap-4">
                        {declinedRequests.map(req => {
                           const student = users.find(u => u.id === req.studentId);
                           const prop = myProperties.find(p => p.id === req.propertyId);
                           return (
                              <div key={req.id} className="p-4 bg-gray-100 rounded-lg flex justify-between items-center text-gray-500">
                                 <div>
                                    <span className="font-semibold text-gray-600">{student?.name}</span> for {prop?.title}
                                 </div>
                                 <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Declined</span>
                              </div>
                           )
                        })}
                     </div>
                  </div>
               )}

            </div>
         )}

         {/* REGISTERED STUDENTS TAB */}
         {activeTab === 'registered-students' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div>
                     <h2 className="text-2xl font-bold text-gray-900">Registered Students</h2>
                     <p className="text-gray-500">Students with confirmed room assignments in your properties</p>
                  </div>
               </div>

               <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                     <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                           <tr>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Full Name</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Email</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Phone Number</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Property</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Room/Bed</th>
                               <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs">Status</th>
                              <th className="px-6 py-4 font-bold text-gray-500 uppercase tracking-wider text-xs text-right">Actions</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                           {bookings
                              .filter(b => (b.status === 'CONFIRMED' || b.status === 'COMPLETED') && myProperties.some(p => p.id === b.propertyId))
                              .map((booking) => {
                                 const student = users.find(u => u.id === booking.studentId);
                                 const property = properties.find(p => p.id === booking.propertyId);
                                 
                                 // Get room or bed name
                                 let roomBedName = 'Whole Unit';
                                 if (booking.roomId && property?.rooms) {
                                    const room = property.rooms.find(r => r.id === booking.roomId);
                                    if (room) {
                                       roomBedName = room.name;
                                       if (booking.bedId && room.beds) {
                                          const bed = room.beds.find(b => b.id === booking.bedId);
                                          if (bed) {
                                             roomBedName = `${room.name} - ${bed.name}`;
                                          }
                                       }
                                    }
                                 } else if (booking.bedId && property?.beds) {
                                    const bed = property.beds.find(b => b.id === booking.bedId);
                                    if (bed) {
                                       roomBedName = bed.name;
                                    }
                                 }

                                 if (!student) return null;

                                 return (
                                    <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                                       <td className="px-6 py-4">
                                          <div className="flex items-center gap-3">
                                             <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                                                {student.name.charAt(0)}
                                             </div>
                                             <p className="font-bold text-gray-900">{student.name}</p>
                                          </div>
                                       </td>
                                       <td className="px-6 py-4">
                                          <p className="text-sm text-gray-600">{student.email}</p>
                                       </td>
                                       <td className="px-6 py-4">
                                          <p className="text-sm font-medium text-gray-700">{student.phoneNumber || 'N/A'}</p>
                                       </td>
                                       <td className="px-6 py-4">
                                          <p className="text-sm font-medium text-gray-900">{property?.title || 'Unknown'}</p>
                                          <p className="text-xs text-gray-500">{property?.location}</p>
                                       </td>
                                       <td className="px-6 py-4">
                                          <Badge color="purple">{roomBedName}</Badge>
                                       </td>
                                       <td className="px-6 py-4">
                                          <Badge color={booking.status === 'COMPLETED' ? 'green' : 'blue'}>
                                             {booking.status}
                                          </Badge>
                                       </td>
                                       <td className="px-6 py-4 text-right">
                                          <div className="flex justify-end gap-2">
                                             <button
                                                className="text-blue-600 hover:bg-blue-50 p-1.5 rounded transition-colors"
                                                onClick={() => setViewingProfile(student.id)}
                                                title="View Profile"
                                             >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                             <button
                                                className="text-green-600 hover:bg-green-50 p-1.5 rounded transition-colors"
                                                onClick={() => {
                                                   setActiveTab('messages');
                                          setSelectedChatUser(student.id);
                                                }}
                                                title="View Messages"
                                              >
                                                <MessageSquare className="w-4 h-4" />
                                             </button>
                                          </div>
                                       </td>
                                    </tr>
                                 );
                              })}
                           {bookings.filter(b => (b.status === 'CONFIRMED' || b.status === 'COMPLETED') && myProperties.some(p => p.id === b.propertyId)).length === 0 && (
                              <tr>
                                 <td colSpan={7} className="p-8 text-center text-gray-500">
                                    No registered students yet.
                                 </td>
                              </tr>
                           )}
                        </tbody>
                     </table>
                  </div>
               </Card>
            </div>
         )}


         {/* ANALYTICS TAB */}
         {activeTab === 'analytics' && (
            <div className="space-y-6 animate-fadeIn">
               <h2 className="text-2xl font-bold text-gray-900">Performance Analytics</h2>

               {/* CSS Bar Chart */}
               <Card className="p-6">
                  <h3 className="font-bold text-gray-900 mb-6">Weekly Engagement</h3>
                  <div className="flex items-end justify-between h-64 gap-2">
                     {[65, 45, 78, 52, 90, 85, 70].map((h, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
                           <div className="w-full bg-blue-50 rounded-t-lg relative h-full flex items-end overflow-hidden">
                              <div
                                 className="w-full bg-[#2f5c96] transition-all duration-1000 group-hover:bg-[#c19cca]"
                                 style={{ height: `${h}%` }}
                              ></div>
                           </div>
                           <span className="text-xs text-gray-500">{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}</span>
                        </div>
                     ))}
                  </div>
               </Card>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="p-6">
                     <h3 className="font-bold text-gray-900 mb-4">Demographics</h3>
                     <div className="space-y-4">
                        <div className="flex justify-between items-center text-sm">
                           <span className="text-gray-600">Local Students</span>
                           <span className="font-bold text-gray-900">75%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                           <div className="bg-[#2f5c96] h-2 rounded-full" style={{ width: '75%' }}></div>
                        </div>

                        <div className="flex justify-between items-center text-sm">
                           <span className="text-gray-600">International</span>
                           <span className="font-bold text-gray-900">25%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                           <div className="bg-[#c19cca] h-2 rounded-full" style={{ width: '25%' }}></div>
                        </div>
                     </div>
                  </Card>
                  <Card className="p-6">
                     <h3 className="font-bold text-gray-900 mb-4">Source</h3>
                     <ul className="space-y-3 text-sm">
                        <li className="flex justify-between py-2 border-b border-gray-50"><span className="text-gray-600">Direct</span><span className="font-bold text-green-600">+450</span></li>
                        <li className="flex justify-between py-2"><span className="text-gray-600">Referral</span><span className="font-bold text-green-600">+85</span></li>
                     </ul>
                  </Card>
               </div>
            </div>
         )}

         {/* MESSAGES TAB */}
         {activeTab === 'messages' && (
            <div className="h-[calc(100vh-200px)] flex flex-col animate-fadeIn">
               <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Inquiries</h2>
               </div>

               <Card className="flex-1 flex overflow-hidden">
                  {/* Inbox List */}
                  <div className="w-full md:w-1/3 border-r border-gray-100 flex flex-col">
                     <div className="p-4 border-b border-gray-100">
                        <div className="relative">
                           <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                           <input type="text" placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2f5c96]" />
                        </div>
                     </div>
                     <div className="overflow-y-auto flex-1">
                        {conversations.length > 0 ? conversations.map(conv => (
                           <div key={conv.userId} onClick={() => setSelectedChatUser(conv.userId)} className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${selectedChatUser === conv.userId ? 'bg-blue-50/50' : ''}`}>
                              <div className="flex justify-between items-start mb-1">
                                 <span className={`text-sm ${conv.unread ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{conv.name}</span>
                                 <span className="text-xs text-gray-400">{new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              <p className="text-xs text-gray-500 truncate">{conv.lastMessage}</p>
                           </div>
                        )) : <div className="p-8 text-center text-gray-500 text-sm">No conversations.</div>}
                     </div>
                  </div>

                  {/* Chat Area */}
                  <div className="hidden md:flex flex-1 flex-col bg-slate-50/30">
                     {selectedChatUser ? (
                        <>
                           <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center">
                              <div>
                                 <h3 className="font-bold text-gray-900">{conversations.find(c => c.userId === selectedChatUser)?.name}</h3>
                                 <p className="text-xs text-green-600 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span> Online</p>
                              </div>
                              <div className="flex gap-2">
                                 <Button size="sm" variant="outline" onClick={() => setViewingProfile(selectedChatUser)}>View Profile</Button>
                              </div>
                           </div>
                           <div className="flex-1 p-6 overflow-y-auto space-y-4">
                              {activeConversation.map(msg => (
                                 <div key={msg.id} className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`p-3 rounded-lg shadow-sm max-w-sm ${msg.senderId === user?.id ? 'bg-[#2f5c96] text-white rounded-br-none' : 'bg-white border border-gray-100 rounded-bl-none'}`}>
                                       <p className="text-sm">{msg.content}</p>
                                       <span className={`text-[10px] block text-right mt-1 ${msg.senderId === user?.id ? 'text-blue-100' : 'text-gray-400'}`}>
                                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                       </span>
                                    </div>
                                 </div>
                              ))}
                           </div>
                           <div className="p-4 bg-white border-t border-gray-100">
                              <div className="flex gap-2">
                                 <input
                                    type="text"
                                    placeholder="Type a message..."
                                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#2f5c96]"
                                    value={chatInput}
                                    onChange={e => setChatInput(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                                 />
                                 <Button onClick={handleSendMessage}><Send className="w-4 h-4" /></Button>
                              </div>
                           </div>
                        </>
                     ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                           <MessageSquare className="w-12 h-12 mb-2 opacity-20" />
                           <p>Select a conversation to start chatting</p>
                        </div>
                     )}
                  </div>
               </Card>
            </div>
         )}

         {/* EMAIL PROPERTIES TAB */}
         {activeTab === 'email-properties' && (
            <div className="space-y-6 animate-fadeIn">
               <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div>
                     <h2 className="text-2xl font-bold text-gray-900">Email Properties to Students</h2>
                     <p className="text-gray-500">Select properties and generate formatted emails for students.</p>
                  </div>
                  <Button onClick={() => setIsEmailComposerOpen(true)} className="w-full sm:w-auto">
                     <Mail className="w-4 h-4 mr-2" /> Compose Email
                  </Button>
               </div>

               {myProperties.length > 0 ? (
                  <Card className="p-6">
                     <h3 className="font-bold text-gray-900 mb-4">Your Available Properties</h3>
                     <div className="grid gap-4 md:grid-cols-2">
                        {myProperties.filter(p => p.status === 'AVAILABLE').map(property => {
                           // Calculate availability
                           let availabilityText = 'Available';
                           if (property.rooms && property.rooms.length > 0) {
                              const availableRooms = property.rooms.filter(r => r.available !== false).length;
                              availabilityText = `${availableRooms} room${availableRooms !== 1 ? 's' : ''} available`;
                           } else if (property.beds && property.beds.length > 0) {
                              const availableBeds = property.beds.filter(b => b.available !== false).length;
                              availabilityText = `${availableBeds} bed${availableBeds !== 1 ? 's' : ''} available`;
                           }

                           return (
                              <Card key={property.id} className="p-4 flex gap-4 hover:shadow-md transition-shadow">
                                 <div className="w-24 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                    <img src={property.image} alt={property.title} className="w-full h-full object-cover" />
                                 </div>
                                 <div className="flex-1">
                                    <h4 className="font-bold text-gray-900 mb-1">{property.title}</h4>
                                    <p className="text-sm text-gray-600 mb-2">{property.locationName || property.location}</p>
                                    <div className="flex items-center gap-2">
                                       <Badge color="green">{availabilityText}</Badge>
                                       <span className="text-sm font-bold text-[#2f5c96]">R{property.price}/mo</span>
                                    </div>
                                 </div>
                              </Card>
                           );
                        })}
                     </div>
                     <div className="mt-6 text-center">
                        <Button onClick={() => setIsEmailComposerOpen(true)} className="w-full sm:w-auto">
                           <Mail className="w-4 h-4 mr-2" /> Create Email with Selected Properties
                        </Button>
                     </div>
                  </Card>
               ) : (
                  <Card className="p-8 text-center bg-gray-50 border-dashed">
                     <Mail className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                     <h3 className="text-lg font-bold text-gray-900 mb-2">No Properties Available</h3>
                     <p className="text-gray-500 mb-6">You need to add properties before you can email them to students.</p>
                     <Button onClick={() => setActiveTab('add-listing')}>
                        <Plus className="w-4 h-4 mr-2" /> Add Your First Property
                     </Button>
                  </Card>
               )}
            </div>
         )}

         {/* SETTINGS TAB */}
         {activeTab === 'settings' && (
            <div className="max-w-2xl animate-fadeIn">
               <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h2>
               <Card className="p-8">
                  <form className="space-y-6">
                     <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Agency Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
                              <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={user?.agencyName} />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">License No.</label>
                              <input type="text" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={user?.licenseNumber} />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                              <input type="tel" className="w-full p-2 border border-gray-300 rounded-md" defaultValue={user?.phoneNumber} />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <input type="email" className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" defaultValue={user?.email} readOnly />
                           </div>
                        </div>
                     </div>
                     <div className="pt-6">
                        <Button>Save Changes</Button>
                     </div>
                  </form>
               </Card>
            </div>
         )}

         {/* Student Profile Modal */}
         {viewingProfile && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
               <Card className="max-w-4xl w-full p-0 flex flex-col md:flex-row overflow-hidden max-h-[90vh] relative animate-scaleIn">
                  <button onClick={() => setViewingProfile(null)} className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                  <div className="md:w-1/2 p-8 overflow-y-auto">
                     <div className="text-center mb-6">
                        <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl text-gray-500 font-bold">
                           {users.find(u => u.id === viewingProfile)?.name.charAt(0)}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{users.find(u => u.id === viewingProfile)?.name}</h3>
                        <p className="text-gray-500">{users.find(u => u.id === viewingProfile)?.email}</p>
                        <Badge className="mt-2">{users.find(u => u.id === viewingProfile)?.role}</Badge>
                     </div>
                     <div className="space-y-4 border-t border-gray-100 pt-4">
                        <div className="flex justify-between items-center">
                           <span className="text-gray-600 flex items-center gap-2"><Building className="w-4 h-4" /> Institution</span>
                           <span className="font-medium">{users.find(u => u.id === viewingProfile)?.institution || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                           <span className="text-gray-600 flex items-center gap-2"><Clock className="w-4 h-4" /> Joined</span>
                           <span className="font-medium">Oct 2023</span>
                        </div>
                     </div>
                     <div className="mt-6">
                        <Button className="w-full" onClick={() => { setActiveTab('messages'); setSelectedChatUser(viewingProfile); setViewingProfile(null); }}>
                           Message Student
                        </Button>
                     </div>
                  </div>
                  <div className="md:w-1/2 bg-slate-50 p-8 border-l border-gray-100 overflow-y-auto">
                     <InternalNotes targetId={viewingProfile} targetType="user" />
                  </div>
               </Card>
            </div>
         )}

         {/* Property Email Composer Modal */}
         {isEmailComposerOpen && (
            <PropertyEmailComposer
               properties={myProperties.filter(p => p.status === 'AVAILABLE')}
               agentName={user?.name || 'Agent'}
               agentPhone={user?.phoneNumber}
               onClose={() => setIsEmailComposerOpen(false)}
            />
         )}

      </DashboardLayout>
   );
};